import {
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { enqueueSnackbar } from 'notistack';
import Iconify from 'src/components/iconify';
import axios, { endpoints, formatEndpoint, INTERNAL_SERVER_ERROR } from 'src/utils/axios';
import useModelData from 'src/hooks/use-model-data';
import { DisplayPageData } from 'src/components/display-page-data';
import { HOST_API } from 'src/config-global';
import { BrandBar, StatusScreen } from 'src/pages/sign-rental-contract';

type InsuranceAttachmentInfo = {
  name: string;
  blankUrl: string;
  returned: boolean;
};

type InsuranceInfoResponse = {
  status: 'pending' | 'expired' | 'submitted' | 'confirmed';
  companyName: string;
  companyLogoUrl: string;
  customerFirstName: string;
  organizationId: number;
  trailerUnitNumber: string;
  attachments: InsuranceAttachmentInfo[];
  customerDocumentCount: number;
  rejectionReason: string;
};

type PageModel = {
  insuranceInfo: InsuranceInfoResponse;
};

export default function Page() {
  const { insuranceUuid } = useParams();

  const dataLoader = useCallback(
    () => ({
      insuranceInfo: axios
        .get(formatEndpoint(endpoints.insuranceInfo.findByUuid, { insuranceUuid }))
        .then((response) => response.data),
    }),
    [insuranceUuid]
  );

  const { data, loaded, error, loading, setShouldRefresh } = useModelData<PageModel>(dataLoader);

  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [doneSubmitting, setDoneSubmitting] = useState(false);

  const insuranceInfo = data?.insuranceInfo;
  const attachments = insuranceInfo?.attachments || [];
  const allReturned = attachments.every((attachment) => attachment.returned);

  const pdfUrl = HOST_API + formatEndpoint(endpoints.insuranceInfo.pdf, { insuranceUuid });

  const uploadReturn = (attachmentIndex: number, file: File) => {
    setUploadingIndex(attachmentIndex);
    axios
      .postForm(
        formatEndpoint(endpoints.insuranceInfo.uploadReturn, { insuranceUuid, attachmentIndex }),
        { file }
      )
      .then(() => {
        setShouldRefresh(true);
      })
      .catch(() => {
        enqueueSnackbar('The file could not be uploaded. Please try again.', { variant: 'error' });
      })
      .finally(() => setUploadingIndex(null));
  };

  const uploadDocument = (file: File) => {
    setUploadingDocument(true);
    axios
      .postForm(formatEndpoint(endpoints.insuranceInfo.uploadDocument, { insuranceUuid }), { file })
      .then(() => {
        setShouldRefresh(true);
      })
      .catch(() => {
        enqueueSnackbar('The file could not be uploaded. Please try again.', { variant: 'error' });
      })
      .finally(() => setUploadingDocument(false));
  };

  const submit = () => {
    if (!allReturned) {
      enqueueSnackbar('Please upload a completed copy of every form first', { variant: 'error' });
      return;
    }
    setSubmitting(true);
    axios
      .post(endpoints.insuranceInfo.submit, { insuranceUuid })
      .then(() => {
        setDoneSubmitting(true);
      })
      .catch((submitError) => {
        if (submitError?.response?.status === 422 && submitError.response.data?.length) {
          submitError.response.data.forEach((validation: { message: string }) =>
            enqueueSnackbar(validation.message, { variant: 'error' })
          );
        } else {
          enqueueSnackbar(INTERNAL_SERVER_ERROR, { variant: 'error' });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Box minHeight="100vh" display="flex" flexDirection="column" sx={{ bgcolor: 'grey.100' }}>
      <BrandBar companyName={insuranceInfo?.companyName} companyLogoUrl={insuranceInfo?.companyLogoUrl} />

      <Box flexGrow={1}>
        <DisplayPageData loading={loading} error={error} data={data}>
          {doneSubmitting && (
            <StatusScreen
              icon="solar:check-circle-bold"
              iconColor="success.main"
              title="Insurance forms submitted"
              message="The rental company is reviewing your forms. You will receive the lease agreement by email once the insurance is confirmed."
            />
          )}
          {!doneSubmitting && loaded && insuranceInfo?.status === 'expired' && (
            <StatusScreen
              icon="solar:clock-circle-bold"
              iconColor="warning.main"
              title="This link has expired"
              message={`Please contact ${insuranceInfo.companyName || 'the rental company'} to request a new link.`}
            />
          )}
          {!doneSubmitting && loaded && insuranceInfo?.status === 'submitted' && (
            <StatusScreen
              icon="solar:clock-circle-bold"
              iconColor="info.main"
              title="Waiting for review"
              message="Your forms were submitted. The rental company is reviewing them."
            />
          )}
          {!doneSubmitting && loaded && insuranceInfo?.status === 'confirmed' && (
            <StatusScreen
              icon="solar:check-circle-bold"
              iconColor="success.main"
              title="Insurance confirmed"
              message="Your insurance was confirmed. The lease agreement will follow by email."
            />
          )}
          {!doneSubmitting && loaded && insuranceInfo?.status === 'pending' && (
            <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
              <Stack gap={2}>
                <Box>
                  <Typography variant="h4" sx={{ textWrap: 'balance' }}>
                    {insuranceInfo.customerFirstName
                      ? `Hi ${insuranceInfo.customerFirstName}, please complete the insurance forms`
                      : 'Please complete the insurance forms'}
                  </Typography>
                  <Typography color="text.secondary" pt={0.5}>
                    {`To rent Trailer ${insuranceInfo.trailerUnitNumber}, download each form below, fill it in, and upload it back.`}
                  </Typography>
                </Box>

                {!!insuranceInfo.rejectionReason && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1.5,
                      bgcolor: 'error.lighter',
                      border: '1px solid',
                      borderColor: 'error.light',
                    }}
                  >
                    <Typography variant="subtitle2" color="error.dark">
                      Your previous submission was not accepted
                    </Typography>
                    <Typography variant="body2" color="error.dark">
                      {`${insuranceInfo.rejectionReason}. Please complete the forms again.`}
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    p: 2,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Iconify icon="solar:document-text-bold" width={18} />
                      <Typography variant="subtitle2">Insurance Request</Typography>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener"
                      endIcon={<Iconify icon="eva:external-link-fill" width={16} />}
                    >
                      Open PDF
                    </Button>
                  </Stack>
                </Box>

                {attachments.map((attachment, index) => (
                  <Box
                    key={index}
                    sx={{
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      p: 2,
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      alignItems={{ sm: 'center' }}
                      justifyContent="space-between"
                      gap={1.5}
                    >
                      <Stack direction="row" alignItems="center" gap={1.5}>
                        <Iconify
                          icon={attachment.returned ? 'solar:check-circle-bold' : 'solar:document-bold'}
                          width={24}
                          color={attachment.returned ? 'success.main' : 'text.secondary'}
                        />
                        <Box>
                          <Typography variant="subtitle2">{attachment.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {attachment.returned
                              ? 'Completed copy uploaded'
                              : 'Download the form, fill it in and upload it back'}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" gap={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          href={attachment.blankUrl}
                          target="_blank"
                          rel="noopener"
                          startIcon={<Iconify icon="solar:download-bold" />}
                        >
                          Download
                        </Button>
                        <Button
                          size="small"
                          variant={attachment.returned ? 'outlined' : 'contained'}
                          component="label"
                          disabled={uploadingIndex !== null}
                          startIcon={
                            uploadingIndex === index ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Iconify icon="solar:upload-bold" />
                            )
                          }
                        >
                          {attachment.returned ? 'Replace' : 'Upload'}
                          <input
                            hidden
                            type="file"
                            accept="application/pdf,image/png,image/jpeg"
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              if (file) {
                                uploadReturn(index, file);
                              }
                              event.target.value = '';
                            }}
                          />
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                ))}

                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    p: 2,
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ sm: 'center' }}
                    justifyContent="space-between"
                    gap={1.5}
                  >
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <Iconify
                        icon={
                          insuranceInfo.customerDocumentCount > 0
                            ? 'solar:check-circle-bold'
                            : 'solar:documents-bold'
                        }
                        width={24}
                        color={
                          insuranceInfo.customerDocumentCount > 0 ? 'success.main' : 'text.secondary'
                        }
                      />
                      <Box>
                        <Typography variant="subtitle2">Additional documents (optional)</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {insuranceInfo.customerDocumentCount > 0
                            ? `${insuranceInfo.customerDocumentCount} ${insuranceInfo.customerDocumentCount === 1 ? 'document' : 'documents'} uploaded`
                            : 'Attach any other documents you want the rental company to review, such as a policy copy or certificate'}
                        </Typography>
                      </Box>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      component="label"
                      disabled={uploadingDocument}
                      startIcon={
                        uploadingDocument ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Iconify icon="solar:upload-bold" />
                        )
                      }
                    >
                      {insuranceInfo.customerDocumentCount > 0 ? 'Add Another' : 'Upload'}
                      <input
                        hidden
                        type="file"
                        accept="application/pdf,image/png,image/jpeg"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) {
                            uploadDocument(file);
                          }
                          event.target.value = '';
                        }}
                      />
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </Container>
          )}
        </DisplayPageData>
      </Box>

      {!doneSubmitting && loaded && insuranceInfo?.status === 'pending' && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -4px 12px rgba(1, 14, 7, 0.06)',
          }}
        >
          <Container maxWidth="md">
            <Stack direction="row" alignItems="center" justifyContent="flex-end" py={1.5}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                disabled={submitting || !allReturned}
                onClick={submit}
                startIcon={submitting ? <CircularProgress size={16} /> : <Iconify icon="solar:check-circle-bold" />}
                sx={{ minWidth: 200 }}
              >
                Submit Forms
              </Button>
            </Stack>
          </Container>
        </Box>
      )}

      <Box py={2} textAlign="center">
        <Typography variant="caption" color="text.disabled">
          Powered by Counting Trucks
        </Typography>
      </Box>
    </Box>
  );
}
